import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { createPortal } from "react-dom";

import axios from "../../helpers/axios";
import { generatePublicUrl } from "../../urlConfig";
import { openOfficeProductGallery } from "../../actions/officeProductGallery.actions";
import { openOfficeProduct3D } from "../../actions/officeProduct3D.actions";

const TYPE_META = {
    order: { label: "Заказы", singular: "Заказ", icon: "▤", placeholder: "Номер заказа, клиент, товар..." },
    product: { label: "Товары", singular: "Товар", icon: "▣", placeholder: "Артикул, название, категория..." },
    warehouse_material: { label: "Склад", singular: "Материал", icon: "▦", placeholder: "Название, SKU, тип материала..." },
    task: { label: "Задания", singular: "Задание", icon: "✓", placeholder: "Название, заказ, товар..." },
    member: { label: "Сотрудники", singular: "Сотрудник", icon: "●", placeholder: "Имя, email, логин..." },
    sales_session: { label: "3D-сессии", singular: "3D-сессия", icon: "◈", placeholder: "Код сессии, название..." },
};

const TASK_STATUS = {
    published: "Доступно",
    accepted: "Принято",
    in_progress: "В работе",
    review: "На проверке",
    done: "Выполнено",
    rework: "На доработке",
    cancelled: "Отменено",
};

const SESSION_STATUS = {
    active: "Активна",
    closed: "Закрыта",
    converted_to_order: "Создан заказ",
    archived: "Архив",
};

const ROLE_LABELS = {
    owner: "Владелец",
    admin: "Администратор",
    manager: "Менеджер",
    sales: "Продажи",
    installer: "Монтажник",
    driver: "Водитель",
    warehouse: "Склад",
    accountant: "Бухгалтер",
    worker: "Работник",
};

const UNIT_LABELS = {
    pcs: "шт.",
    m: "м",
    m2: "м²",
    m3: "м³",
    kg: "кг",
    t: "т",
    l: "л",
    bag: "меш.",
    bucket: "вед.",
    sheet: "лист",
    set: "компл.",
};

const MATERIAL_TYPE_LABELS = {
    raw: "Сырьё",
    consumable: "Расходник",
    service: "Услуга",
    component: "Компонент",
    packaging: "Упаковка",
    other: "Другое",
};

const STOCK_FILTER_DEFAULTS = [
    { id: "all", label: "Все" },
    { id: "need", label: "Нужно докупить" },
    { id: "out", label: "Нет в наличии" },
];

const formatQty = (value, unit) => {
    const number = Number(value || 0);
    const formatted = Number.isInteger(number)
        ? number.toLocaleString("ru-RU")
        : number.toLocaleString("ru-RU", { maximumFractionDigits: 3 });

    return `${formatted} ${UNIT_LABELS[unit] || unit || ""}`.trim();
};

const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleDateString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};

const imageSrc = (value) => {
    if (!value) return "";
    if (/^(https?:)?\/\//i.test(value) || value.startsWith("data:") || value.startsWith("blob:")) return value;
    return generatePublicUrl(value);
};

function ResultIcon({ item, onOpenGallery, onOpen3D }) {
    const src = imageSrc(item?.image);
    const meta = TYPE_META[item?.type] || { icon: "◆", singular: "Объект" };
    const canOpenGallery = item?.type === "product" && !!item?.id;
    const canOpen3D = canOpenGallery && item?.payload?.hasModel === true;

    const handleOpenGallery = (event) => {
        if (!canOpenGallery) return;

        event.preventDefault();
        event.stopPropagation();
        onOpenGallery?.(item);
    };

    const handleOpen3D = (event) => {
        if (!canOpen3D) return;

        event.preventDefault();
        event.stopPropagation();
        onOpen3D?.(item);
    };

    return (
        <div
            className={[
                "chat-entity-picker-AO__item-icon",
                canOpenGallery ? "is-product-gallery" : "",
            ].filter(Boolean).join(" ")}
            onClick={canOpenGallery ? handleOpenGallery : undefined}
            title={canOpenGallery ? "Открыть фотографии товара" : undefined}
        >
            {src ? (
                <img
                    src={src}
                    alt={item?.title || meta.singular}
                    loading="lazy"
                    decoding="async"
                    onError={(event) => {
                        event.currentTarget.style.display = "none";
                    }}
                />
            ) : (
                <span>{meta.icon}</span>
            )}

            {canOpenGallery ? (
                <i className="product-gallery-trigger-AO__zoom" aria-hidden="true">⌕</i>
            ) : null}

            {canOpen3D ? (
                <i
                    className="product-3d-trigger-AO__button"
                    onClick={handleOpen3D}
                    title="Открыть товар в 3D"
                    aria-label="Открыть товар в 3D"
                >
                    3D
                </i>
            ) : null}
        </div>
    );
}

function ResultMeta({ item }) {
    const p = item?.payload || {};

    if (item.type === "order") {
        return (
            <>
                {p.status ? <span>{p.status}</span> : null}
                {p.customerName ? <span>{p.customerName}</span> : null}
                {p.createdAt ? <span>{formatDate(p.createdAt)}</span> : null}
            </>
        );
    }

    if (item.type === "product") {
        const displaySku = String(
            p.displaySku !== undefined && p.displaySku !== null && p.displaySku !== ""
                ? p.displaySku
                : p.article ?? ""
        ).trim();

        return (
            <>
                {displaySku ? <span>Арт. {displaySku}</span> : null}
                {p.categoryName ? <span>{p.categoryName}</span> : null}
                {p.materialName ? <span>{p.materialName}</span> : null}
                {p.hasModel ? <span>3D</span> : null}
                {p.inOfficeCatalog === true ? (
                    <span className="is-office-catalog">✓ В каталоге офиса</span>
                ) : null}
                {p.inOfficeCatalog === false ? (
                    <span className="is-outside-catalog">Не добавлен в каталог</span>
                ) : null}
            </>
        );
    }

    if (item.type === "warehouse_material") {
        const unit = p.unit || "pcs";

        return (
            <>
                {p.sku ? <span>SKU {p.sku}</span> : null}
                {p.materialType ? <span>{MATERIAL_TYPE_LABELS[p.materialType] || p.materialType}</span> : null}
                {p.isStockTracked === false ? (
                    <span className="is-stock-untracked">Учёт выключен</span>
                ) : (
                    <>
                        <span>Доступно {formatQty(p.available, unit)}</span>
                        {Number(p.reserved || 0) > 0 ? <span>Резерв {formatQty(p.reserved, unit)}</span> : null}
                        {Number(p.minStock || 0) > 0 ? <span>Минимум {formatQty(p.minStock, unit)}</span> : null}
                        {p.stockState === "out" ? (
                            <span className="is-stock-out">Нет в наличии</span>
                        ) : p.stockState === "low" ? (
                            <span className="is-stock-low">Нужно докупить {formatQty(p.needToBuy, unit)}</span>
                        ) : (
                            <span className="is-stock-ok">Остаток в норме</span>
                        )}
                    </>
                )}
            </>
        );
    }

    if (item.type === "task") {
        return (
            <>
                {p.status ? <span>{TASK_STATUS[p.status] || p.status}</span> : null}
                {p.checklistTotal !== undefined ? <span>Чек-лист {p.checklistDone || 0}/{p.checklistTotal || 0}</span> : null}
                {p.dueAt ? <span>до {formatDate(p.dueAt)}</span> : null}
            </>
        );
    }

    if (item.type === "member") {
        return (
            <>
                {p.role ? <span>{ROLE_LABELS[p.role] || p.role}</span> : null}
                {p.email ? <span>{p.email}</span> : null}
            </>
        );
    }

    if (item.type === "sales_session") {
        return (
            <>
                {p.sessionId ? <span>{p.sessionId}</span> : null}
                {p.status ? <span>{SESSION_STATUS[p.status] || p.status}</span> : null}
                {p.currentTab ? <span>{p.currentTab}</span> : null}
            </>
        );
    }

    return null;
}

export default function ChatEntityPickerModal({
    open,
    officeId,
    onClose,
    onSelect,
    sending = false,
}) {
    const dispatch = useDispatch();
    const [types, setTypes] = useState([]);
    const [activeType, setActiveType] = useState("");
    const [productPicker, setProductPicker] = useState({
        defaultScope: "office_catalog",
        scopes: [
            { id: "office_catalog", label: "Каталог офиса" },
            { id: "office_categories", label: "Категории офиса" },
        ],
        categories: [],
    });
    const [productScope, setProductScope] = useState("office_catalog");
    const [productCategoryId, setProductCategoryId] = useState("");
    const [warehousePicker, setWarehousePicker] = useState({
        defaultWarehouseId: "all",
        defaultFilter: "all",
        filters: STOCK_FILTER_DEFAULTS,
        warehouses: [],
    });
    const [warehouseId, setWarehouseId] = useState("all");
    const [warehouseStockFilter, setWarehouseStockFilter] = useState("all");
    const [warehouseCounts, setWarehouseCounts] = useState({
        all: 0,
        need: 0,
        out: 0,
    });
    const [query, setQuery] = useState("");
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMeta, setLoadingMeta] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [selectingId, setSelectingId] = useState("");
    const [error, setError] = useState("");

    const activeMeta = TYPE_META[activeType] || TYPE_META.order;

    useEffect(() => {
        if (!open || !officeId) return undefined;

        let mounted = true;
        setLoadingMeta(true);
        setError("");
        setTypes([]);
        setActiveType("");
        setProductPicker({
            defaultScope: "office_catalog",
            scopes: [
                { id: "office_catalog", label: "Каталог офиса" },
                { id: "office_categories", label: "Категории офиса" },
            ],
            categories: [],
        });
        setProductScope("office_catalog");
        setProductCategoryId("");
        setWarehousePicker({
            defaultWarehouseId: "all",
            defaultFilter: "all",
            filters: STOCK_FILTER_DEFAULTS,
            warehouses: [],
        });
        setWarehouseId("all");
        setWarehouseStockFilter("all");
        setWarehouseCounts({ all: 0, need: 0, out: 0 });
        setQuery("");
        setItems([]);
        setPage(1);
        setHasMore(false);

        axios
            .get(`/office/${officeId}/chat-entities/meta`)
            .then((response) => {
                if (!mounted) return;

                const nextTypes = (response.data?.types || [])
                    .map((item) => item?.type)
                    .filter((type) => TYPE_META[type]);

                const rawProductPicker = response.data?.productPicker || {};
                const nextScopes = Array.isArray(rawProductPicker.scopes) && rawProductPicker.scopes.length
                    ? rawProductPicker.scopes
                    : [
                        { id: "office_catalog", label: "Каталог офиса" },
                        { id: "office_categories", label: "Категории офиса" },
                    ];
                const nextCategories = Array.isArray(rawProductPicker.categories)
                    ? rawProductPicker.categories
                    : [];
                const requestedDefaultScope = String(rawProductPicker.defaultScope || "office_catalog");
                const nextDefaultScope = nextScopes.some((scope) => scope?.id === requestedDefaultScope)
                    ? requestedDefaultScope
                    : "office_catalog";

                setTypes(nextTypes);
                setActiveType(nextTypes[0] || "");
                setProductPicker({
                    defaultScope: nextDefaultScope,
                    scopes: nextScopes,
                    categories: nextCategories,
                });
                setProductScope(nextDefaultScope);
                setProductCategoryId("");

                const rawWarehousePicker = response.data?.warehousePicker || {};
                const nextWarehouseFilters = Array.isArray(rawWarehousePicker.filters) && rawWarehousePicker.filters.length
                    ? rawWarehousePicker.filters
                    : STOCK_FILTER_DEFAULTS;
                const nextWarehouses = Array.isArray(rawWarehousePicker.warehouses)
                    ? rawWarehousePicker.warehouses
                    : [];
                const nextWarehouseId = String(rawWarehousePicker.defaultWarehouseId || "all");
                const nextWarehouseFilter = String(rawWarehousePicker.defaultFilter || "all");

                setWarehousePicker({
                    defaultWarehouseId: nextWarehouseId,
                    defaultFilter: nextWarehouseFilter,
                    filters: nextWarehouseFilters,
                    warehouses: nextWarehouses,
                });
                setWarehouseId(nextWarehouseId);
                setWarehouseStockFilter(nextWarehouseFilter);
                setWarehouseCounts({ all: 0, need: 0, out: 0 });
            })
            .catch((requestError) => {
                if (!mounted) return;
                setError(
                    requestError?.response?.data?.error ||
                    requestError?.response?.data?.message ||
                    requestError?.message ||
                    "Не удалось открыть поиск объектов."
                );
            })
            .finally(() => {
                if (mounted) setLoadingMeta(false);
            });

        return () => {
            mounted = false;
        };
    }, [open, officeId]);

    useEffect(() => {
        if (!open || !officeId || !activeType) return undefined;

        let mounted = true;
        const timer = window.setTimeout(() => {
            setLoading(true);
            setError("");
            setItems([]);
            setPage(1);

            axios
                .get(`/office/${officeId}/chat-entities/${activeType}`, {
                    params: {
                        q: query.trim(),
                        page: 1,
                        limit: 25,
                        ...(activeType === "product" ? {
                            productScope,
                            categoryId: productScope === "office_categories"
                                ? productCategoryId
                                : "",
                        } : {}),
                        ...(activeType === "warehouse_material" ? {
                            warehouseId,
                            stockFilter: warehouseStockFilter,
                        } : {}),
                    },
                })
                .then((response) => {
                    if (!mounted) return;
                    setItems(Array.isArray(response.data?.items) ? response.data.items : []);
                    setHasMore(!!response.data?.hasMore);
                    setPage(1);

                    if (activeType === "warehouse_material") {
                        const counts = response.data?.warehousePicker?.counts || {};
                        setWarehouseCounts({
                            all: Number(counts.all || 0),
                            need: Number(counts.need || 0),
                            out: Number(counts.out || 0),
                        });
                    }
                })
                .catch((requestError) => {
                    if (!mounted) return;
                    setError(
                        requestError?.response?.data?.error ||
                        requestError?.response?.data?.message ||
                        requestError?.message ||
                        "Не удалось выполнить поиск."
                    );
                })
                .finally(() => {
                    if (mounted) setLoading(false);
                });
        }, 240);

        return () => {
            mounted = false;
            window.clearTimeout(timer);
        };
    }, [
        open,
        officeId,
        activeType,
        query,
        productScope,
        productCategoryId,
        warehouseId,
        warehouseStockFilter,
    ]);

    useEffect(() => {
        if (!open || typeof document === "undefined") return undefined;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKeyDown = (event) => {
            if (event.key === "Escape" && !sending && !selectingId) {
                onClose?.();
            }
        };

        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open, onClose, sending, selectingId]);

    const groupedSummary = useMemo(() => {
        const label = TYPE_META[activeType]?.label || "Объекты";

        if (activeType === "warehouse_material") {
            const warehouseLabel = warehouseId === "all"
                ? "Все склады"
                : (warehousePicker.warehouses || [])
                    .find((warehouse) => String(warehouse?.id) === String(warehouseId))?.name || "Склад";

            const filterLabel = (warehousePicker.filters || [])
                .find((filter) => filter?.id === warehouseStockFilter)?.label || "Все";

            return [
                label,
                warehouseLabel,
                filterLabel,
                `показано ${items.length}`,
            ].filter(Boolean).join(" · ");
        }

        if (activeType !== "product") {
            return `${label} · показано ${items.length}`;
        }

        const scopeLabel = (productPicker.scopes || [])
            .find((scope) => scope?.id === productScope)?.label || "Каталог офиса";

        const categoryLabel = productScope === "office_categories" && productCategoryId
            ? (productPicker.categories || [])
                .find((category) => String(category?.id) === String(productCategoryId))?.name || ""
            : "";

        return [
            label,
            scopeLabel,
            categoryLabel,
            `показано ${items.length}`,
        ].filter(Boolean).join(" · ");
    }, [
        activeType,
        items.length,
        productPicker.scopes,
        productPicker.categories,
        productScope,
        productCategoryId,
        warehouseId,
        warehouseStockFilter,
        warehousePicker.warehouses,
        warehousePicker.filters,
    ]);

    if (!open || typeof document === "undefined") return null;

    const loadMore = async () => {
        if (!hasMore || loadingMore || !activeType) return;

        const nextPage = page + 1;
        setLoadingMore(true);
        setError("");

        try {
            const response = await axios.get(`/office/${officeId}/chat-entities/${activeType}`, {
                params: {
                    q: query.trim(),
                    page: nextPage,
                    limit: 25,
                    ...(activeType === "product" ? {
                        productScope,
                        categoryId: productScope === "office_categories"
                            ? productCategoryId
                            : "",
                    } : {}),
                    ...(activeType === "warehouse_material" ? {
                        warehouseId,
                        stockFilter: warehouseStockFilter,
                    } : {}),
                },
            });

            const nextItems = Array.isArray(response.data?.items) ? response.data.items : [];
            setItems((prev) => [...prev, ...nextItems]);
            setPage(nextPage);
            setHasMore(!!response.data?.hasMore);

            if (activeType === "warehouse_material") {
                const counts = response.data?.warehousePicker?.counts || {};
                setWarehouseCounts({
                    all: Number(counts.all || 0),
                    need: Number(counts.need || 0),
                    out: Number(counts.out || 0),
                });
            }
        } catch (requestError) {
            setError(
                requestError?.response?.data?.error ||
                requestError?.response?.data?.message ||
                requestError?.message ||
                "Не удалось загрузить следующую страницу."
            );
        } finally {
            setLoadingMore(false);
        }
    };

    const selectItem = async (item) => {
        if (!item?.id || sending || selectingId) return;

        setSelectingId(String(item.id));
        setError("");

        try {
            const selection = item?.shareId
                ? { ...item, id: item.shareId }
                : item;

            const success = await onSelect?.(selection);
            if (success !== false) onClose?.();
        } catch (requestError) {
            setError(
                requestError?.response?.data?.error ||
                requestError?.response?.data?.message ||
                requestError?.message ||
                "Не удалось прикрепить объект."
            );
        } finally {
            setSelectingId("");
        }
    };

    const openProductGallery = (item) => {
        if (!item?.id || !officeId) return;

        dispatch(openOfficeProductGallery({
            officeId,
            productId: item.id,
            title: item.title || "Товар",
            thumbnail: item.image || "",
            source: "chat-entity-picker",
        }));
    };

    const openProduct3D = (item) => {
        if (!item?.id || !officeId || item?.payload?.hasModel !== true) return;

        dispatch(openOfficeProduct3D({
            officeId,
            productId: item.id,
            title: item.title || "Товар",
            source: "chat-entity-picker",
            product: item,
        }));
    };

    return createPortal(
        <div className="chat-entity-picker-AO" role="dialog" aria-modal="true">
            <button
                type="button"
                className="chat-entity-picker-AO__backdrop"
                onClick={() => !sending && !selectingId && onClose?.()}
                aria-label="Закрыть"
            />

            <section className="chat-entity-picker-AO__dialog">
                <header className="chat-entity-picker-AO__head">
                    <div>
                        <span>Прикрепить объект офиса</span>
                        <h2>Что отправить в чат?</h2>
                        <p>Поиск идёт по текущему офису и учитывает права доступа.</p>
                    </div>
                    <button type="button" onClick={onClose} disabled={sending || !!selectingId}>×</button>
                </header>

                {loadingMeta ? (
                    <div className="chat-entity-picker-AO__state">Проверяем доступные разделы...</div>
                ) : error && !types.length ? (
                    <div className="chat-entity-picker-AO__state is-error">{error}</div>
                ) : !types.length ? (
                    <div className="chat-entity-picker-AO__state">Нет доступных объектов для прикрепления.</div>
                ) : (
                    <>
                        <div className="chat-entity-picker-AO__tabs">
                            {types.map((type) => {
                                const meta = TYPE_META[type];
                                return (
                                    <button
                                        type="button"
                                        key={type}
                                        className={activeType === type ? "is-active" : ""}
                                        onClick={() => {
                                            setActiveType(type);
                                            setQuery("");

                                            if (type === "warehouse_material") {
                                                setWarehouseId(warehousePicker.defaultWarehouseId || "all");
                                                setWarehouseStockFilter(warehousePicker.defaultFilter || "all");
                                            }
                                        }}
                                    >
                                        <span>{meta.icon}</span>
                                        {meta.label}
                                    </button>
                                );
                            })}
                        </div>

                        {activeType === "product" ? (
                            <div className="chat-entity-picker-AO__product-controls">
                                <div
                                    className="chat-entity-picker-AO__product-scopes"
                                    role="tablist"
                                    aria-label="Источник товаров"
                                >
                                    {(productPicker.scopes || []).map((scope) => (
                                        <button
                                            type="button"
                                            key={scope.id}
                                            className={productScope === scope.id ? "is-active" : ""}
                                            onClick={() => {
                                                setProductScope(scope.id);
                                                if (scope.id === "office_catalog") {
                                                    setProductCategoryId("");
                                                }
                                            }}
                                        >
                                            {scope.label}
                                        </button>
                                    ))}
                                </div>

                                {productScope === "office_categories" ? (
                                    <label className="chat-entity-picker-AO__product-category">
                                        <span>Категория</span>
                                        <select
                                            value={productCategoryId}
                                            onChange={(event) => setProductCategoryId(event.target.value)}
                                            disabled={!(productPicker.categories || []).length}
                                        >
                                            <option value="">Все категории офиса</option>
                                            {(productPicker.categories || []).map((category) => (
                                                <option key={category.id} value={category.id}>
                                                    {category.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                ) : (
                                    <div className="chat-entity-picker-AO__product-note">
                                        Товары, созданные офисом или добавленные в его каталог.
                                    </div>
                                )}

                                {productScope === "office_categories" && !(productPicker.categories || []).length ? (
                                    <div className="chat-entity-picker-AO__product-note is-warning">
                                        У текущего офиса пока нет подключённых категорий.
                                    </div>
                                ) : null}
                            </div>
                        ) : null}

                        {activeType === "warehouse_material" ? (
                            <div className="chat-entity-picker-AO__warehouse-controls">
                                <div className="chat-entity-picker-AO__warehouse-filters" role="tablist" aria-label="Состояние остатков">
                                    {(warehousePicker.filters || STOCK_FILTER_DEFAULTS).map((filter) => (
                                        <button
                                            type="button"
                                            key={filter.id}
                                            className={warehouseStockFilter === filter.id ? "is-active" : ""}
                                            onClick={() => setWarehouseStockFilter(filter.id)}
                                        >
                                            {filter.label}
                                            <i>{Number(warehouseCounts?.[filter.id] || 0)}</i>
                                        </button>
                                    ))}
                                </div>

                                <label className="chat-entity-picker-AO__warehouse-select">
                                    <span>Склад</span>
                                    <select
                                        value={warehouseId}
                                        onChange={(event) => setWarehouseId(event.target.value)}
                                    >
                                        <option value="all">Все склады</option>
                                        {(warehousePicker.warehouses || []).map((warehouse) => (
                                            <option key={warehouse.id} value={warehouse.id}>
                                                {warehouse.name}{warehouse.isActive === false ? " · выключен" : ""}
                                            </option>
                                        ))}
                                    </select>
                                </label>

                                {!(warehousePicker.warehouses || []).length ? (
                                    <div className="chat-entity-picker-AO__warehouse-note">
                                        В офисе пока нет складов. Материалы будут показаны с нулевыми остатками.
                                    </div>
                                ) : null}
                            </div>
                        ) : null}

                        <div className="chat-entity-picker-AO__search">
                            <span>⌕</span>
                            <input
                                autoFocus
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder={activeMeta.placeholder}
                            />
                            {query ? (
                                <button type="button" onClick={() => setQuery("")} aria-label="Очистить">×</button>
                            ) : null}
                        </div>

                        <div className="chat-entity-picker-AO__subhead">
                            <strong>{groupedSummary}</strong>
                            <span>Нажми на строку, чтобы отправить карточку</span>
                        </div>

                        <div className="chat-entity-picker-AO__body">
                            {loading ? (
                                <div className="chat-entity-picker-AO__state">Ищем...</div>
                            ) : error ? (
                                <div className="chat-entity-picker-AO__state is-error">{error}</div>
                            ) : items.length ? (
                                <div className="chat-entity-picker-AO__list">
                                    {items.map((item) => (
                                        <button
                                            type="button"
                                            key={`${item.type}:${item.id}`}
                                            className={[
                                                "chat-entity-picker-AO__item",
                                                item.type === "product" ? "is-product-result" : "",
                                                item.type === "warehouse_material" ? `is-warehouse-result is-stock-${item?.payload?.stockState || "ok"}` : "",
                                            ].filter(Boolean).join(" ")}
                                            onClick={() => selectItem(item)}
                                            disabled={sending || !!selectingId}
                                        >
                                            <ResultIcon item={item} onOpenGallery={openProductGallery} onOpen3D={openProduct3D} />

                                            <div className="chat-entity-picker-AO__item-main">
                                                <strong>{item.title || activeMeta.singular}</strong>
                                                {item.subtitle ? <p>{item.subtitle}</p> : null}
                                                <div className="chat-entity-picker-AO__item-meta">
                                                    <ResultMeta item={item} />
                                                </div>
                                            </div>

                                            <div className="chat-entity-picker-AO__item-action">
                                                {selectingId === String(item.id) ? <i /> : <span>Прикрепить</span>}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="chat-entity-picker-AO__state">
                                    Ничего не найдено.
                                </div>
                            )}

                            {hasMore && !loading ? (
                                <button
                                    type="button"
                                    className="chat-entity-picker-AO__more"
                                    onClick={loadMore}
                                    disabled={loadingMore || sending || !!selectingId}
                                >
                                    {loadingMore ? "Загружаем..." : "Показать ещё"}
                                </button>
                            ) : null}
                        </div>
                    </>
                )}
            </section>
        </div>,
        document.body
    );
}
